import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Target, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';

const springGentle = { type: 'spring', stiffness: 300, damping: 35 };

type Status = 'loading' | 'success' | 'error';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('loading');
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    // Root cause fix: main.tsx shim rewrites the hash BEFORE createClient()
    // can read it asynchronously. So Supabase never auto-detects the token.
    // Fix: main.tsx now extracts tokens into sessionStorage BEFORE rewriting.
    // Here we call setSession() explicitly with those saved tokens.

    const verify = async () => {
      try {
        // Fast path: already have a session (page reload / already signed in)
        const { data: { session: existing } } = await supabase.auth.getSession();
        if (existing) { setStatus('success'); return; }

        // Main path: use tokens saved by main.tsx shim
        const accessToken  = sessionStorage.getItem('_sb_access_token');
        const refreshToken = sessionStorage.getItem('_sb_refresh_token') ?? '';

        if (!accessToken) {
          // No tokens found — link was already used or expired
          setStatus('error');
          return;
        }

        // Remove immediately so back-nav / refresh can't re-use them
        sessionStorage.removeItem('_sb_access_token');
        sessionStorage.removeItem('_sb_refresh_token');

        const { error } = await supabase.auth.setSession({
          access_token:  accessToken,
          refresh_token: refreshToken,
        });

        setStatus(error ? 'error' : 'success');
      } catch {
        setStatus('error');
      }
    };

    verify();
  }, []);

  // Auto-redirect countdown once verified
  useEffect(() => {
    if (status !== 'success') return;
    if (countdown <= 0) { navigate('/profile'); return; }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [status, countdown, navigate]);

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4">

      {/* Logo */}
      <Link to="/" className="flex items-center gap-2.5 mb-12">
        <div
          className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center"
          style={{ boxShadow: '0 4px 12px color-mix(in srgb, var(--primary) 40%, transparent)' }}
        >
          <Target className="w-5 h-5 text-primary-foreground" />
        </div>
        <span className="font-bold text-xl text-foreground">Pivot-Mate</span>
      </Link>

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 28, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={springGentle}
        className="w-full max-w-md bg-card border border-border rounded-2xl p-10 text-center"
        style={{ boxShadow: '0 8px 40px color-mix(in srgb, var(--primary) 10%, transparent)' }}
      >
        {/* ── Loading ── */}
        {status === 'loading' && (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center gap-5"
          >
            <div
              className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center"
            >
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Verifying your email…
              </h1>
              <p className="text-muted-foreground text-sm">
                Just a moment while we confirm your account.
              </p>
            </div>
          </motion.div>
        )}

        {/* ── Success ── */}
        {status === 'success' && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={springGentle}
            className="flex flex-col items-center gap-5"
          >
            {/* Animated tick */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 500, damping: 25, delay: 0.1 }}
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ background: 'color-mix(in srgb, var(--primary) 12%, transparent)' }}
            >
              <CheckCircle2 className="w-8 h-8 text-primary" />
            </motion.div>

            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Email confirmed! 🎉
              </h1>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Your Pivot-Mate account is now active. Let's set up your profile so partners can find you.
              </p>
            </div>

            {/* Countdown bar */}
            <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
              <motion.div
                className="h-full bg-primary rounded-full"
                initial={{ width: '100%' }}
                animate={{ width: '0%' }}
                transition={{ duration: 5, ease: 'linear' }}
              />
            </div>
            <p className="text-xs text-muted-foreground -mt-2">
              Redirecting to your profile in {countdown}s…
            </p>

            <div className="flex flex-col sm:flex-row gap-3 w-full mt-1">
              <Button
                className="flex-1 rounded-xl font-semibold h-11"
                onClick={() => navigate('/profile')}
                style={{ boxShadow: '0 4px 12px color-mix(in srgb, var(--primary) 35%, transparent)' }}
              >
                Complete My Profile
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                className="flex-1 rounded-xl h-11"
                onClick={() => navigate('/search')}
              >
                Find Partners
              </Button>
            </div>
          </motion.div>
        )}

        {/* ── Error ── */}
        {status === 'error' && (
          <motion.div
            key="error"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={springGentle}
            className="flex flex-col items-center gap-5"
          >
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ background: 'color-mix(in srgb, var(--destructive) 12%, transparent)' }}
            >
              <XCircle className="w-8 h-8 text-destructive" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Link expired or invalid
              </h1>
              <p className="text-muted-foreground text-sm leading-relaxed">
                This confirmation link may have already been used or has expired.
                Please sign in — if your email is confirmed you'll be let right in.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full mt-1">
              <Button
                className="flex-1 rounded-xl font-semibold h-11"
                onClick={() => navigate('/auth')}
                style={{ boxShadow: '0 4px 12px color-mix(in srgb, var(--primary) 35%, transparent)' }}
              >
                Go to Sign In
              </Button>
              <Button
                variant="outline"
                className="flex-1 rounded-xl h-11"
                onClick={() => navigate('/')}
              >
                Back to Home
              </Button>
            </div>
          </motion.div>
        )}
      </motion.div>

      <p className="mt-8 text-xs text-muted-foreground">
        © 2026 Pivot-Mate. All rights reserved.
      </p>
    </div>
  );
}
